/**
 * The rules the portal follows when it is handed someone else's redirect.
 *
 * The customer portal is the browser leg of a desktop app's OAuth flow, so
 * it is handed a `redirect_uri` by an untrusted caller on every authorize
 * request. The rule that makes that safe is that this portal NEVER navigates
 * to a redirect_uri itself: it forwards the request to
 * /v1/auth/oauth/authorize, and the platform — which holds the registered
 * callback list — is the only thing that decides where the browser lands.
 * Everything here exists to keep that rule enforceable.
 */

/**
 * The parameters forwarded to /authorize, as an allow-list.
 *
 * An allow-list rather than "pass the query string along" because the second
 * form lets a caller smuggle anything it likes into a platform endpoint by
 * putting it in a link to the portal. Unrecognised parameters are dropped.
 */
export const AUTHORIZE_PARAMS = [
  // The operator company whose client_id namespace the rest of this request
  // is read in. It leads the list because dropping it is SILENT: client_id is
  // unique per tenant, an absent handle means the platform's own, and a
  // desktop client that correctly sent one and had it filtered out here is
  // answered "that application is not registered" — a sentence about the
  // client, for a mistake in the forwarding.
  "tenant",
  "client_id",
  "redirect_uri",
  "state",
  "code_challenge",
  "code_challenge_method",
  "scope",
  "response_type",
  "installation_id",
  "installation_public_key",
] as const;

export type AuthorizeParam = (typeof AUTHORIZE_PARAMS)[number];

/** The authorize screen's search parameters: the OAuth request, as strings. */
export type AuthorizeSearch = Partial<Record<AuthorizeParam, string>>;

/** The endpoint that turns a consent into a code and a redirect home. */
export const AUTHORIZE_ENDPOINT = "/v1/auth/oauth/authorize";

export function parseAuthorizeSearch(search: Record<string, unknown>): AuthorizeSearch {
  const parsed: AuthorizeSearch = {};
  for (const key of AUTHORIZE_PARAMS) {
    const value = search[key];
    if (typeof value === "string" && value !== "") {
      parsed[key] = value;
    }
  }
  return parsed;
}

/** True when there is enough here to be an authorize request at all. */
export function isAuthorizeRequest(search: AuthorizeSearch): boolean {
  return Boolean(search.client_id && search.redirect_uri);
}

/**
 * handoffURL is the same-origin URL the browser is sent to when the customer
 * approves. It is relative on purpose — the dev server proxies /v1 to the
 * platform, so the SSO cookie travels as a first-party cookie.
 */
export function handoffURL(search: AuthorizeSearch): string {
  const params = new URLSearchParams();
  for (const key of AUTHORIZE_PARAMS) {
    const value = search[key];
    if (value !== undefined) {
      params.set(key, value);
    }
  }
  return `${AUTHORIZE_ENDPOINT}?${params.toString()}`;
}

/**
 * describeCallback renders a redirect_uri for a human to check, and returns
 * undefined for one that will not parse.
 *
 * It is shown, never followed. Displaying only the origin (and flagging the
 * loopback address the desktop client actually listens on) is what lets
 * someone notice that "the app on this machine" is really a website.
 */
export function describeCallback(redirectURI: string | undefined): {
  origin: string;
  loopback: boolean;
} | undefined {
  if (!redirectURI) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(redirectURI);
  } catch {
    return undefined;
  }
  const loopback = url.hostname === "127.0.0.1" || url.hostname === "localhost" || url.hostname === "[::1]";
  return { origin: url.origin, loopback };
}

/**
 * safeReturnPath admits only a same-origin, absolute PATH — the portal's own
 * "where was I" parameter.
 *
 * "/..." is the whole allow-list. "//evil.example" is a protocol-relative URL
 * that a browser reads as another origin, and "/\evil.example" is the same
 * trick spelled with the separator some parsers normalise. Both start with a
 * slash and neither is this site, which is why the check is on the SECOND
 * character too.
 */
export function safeReturnPath(next: unknown): string | undefined {
  if (typeof next !== "string" || next === "") {
    return undefined;
  }
  if (!next.startsWith("/") || next.startsWith("//") || next.startsWith("/\\")) {
    return undefined;
  }
  return next;
}

/**
 * The authorize request a visitor was sent to sign in FOR, read back off the
 * return path.
 *
 * It exists because the sign-in has to open the session against the right
 * SUITE. `resolveSuite` on the server takes `tenant` and `client_id` and, when
 * both are present, resolves the suite from that exact client — which is the
 * suite `/authorize` will compare the session against a moment later. Signing
 * in without them opens a session for the platform's own tenant, and the
 * authorize that follows is refused for a mismatch the person cannot see and
 * did not cause.
 *
 * The `next` path is the only place that information survives: the guard that
 * bounces an unauthenticated visitor here carries the whole pending URL in it
 * and nothing else. Parsed through URLSearchParams against a throwaway base,
 * because `next` is a PATH — it has no origin of its own, and inventing one
 * here is safe precisely because nothing navigates to the result.
 *
 * Only the two fields are read. The rest of the authorize request is the
 * platform's business and re-reading it here would be a second parser to keep
 * in step with AUTHORIZE_PARAMS.
 */
export function pendingAuthorize(next: unknown): { tenant?: string; clientID?: string } {
  const path = safeReturnPath(next);
  if (!path) {
    return {};
  }
  const query = new URLSearchParams(path.slice(path.indexOf("?") + 1));
  if (path.indexOf("?") < 0) {
    return {};
  }
  const tenant = query.get("tenant") ?? undefined;
  const clientID = query.get("client_id") ?? undefined;
  return {
    tenant: tenant === "" ? undefined : tenant,
    clientID: clientID === "" ? undefined : clientID,
  };
}

/** Where we are now, in the form safeReturnPath will accept back. */
export function currentReturnPath(): string {
  return window.location.pathname + window.location.search;
}
